import { createContext, useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type {
    FieldRegistryEntrySource,
    FieldRegistryExitMethod,
    FieldRegistryOrigin,
} from '/@/store/tracking/events/field-registry';
import type { VisitReportElementType, VisitReportEntrySource } from '/@/store/tracking/events/visit-report';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import {
    type FieldRegistryFlow,
    flowEventProps,
} from '/@/views/fields/field-monitoring/components/detail/registries/components/add/flow-tracking';
import { type FieldObservation, type SectionId, TEXTS_SECTION } from './edit/types';

/**
 * One attempt at a new report, from the field selection to the editor. It travels between the two
 * pages in the navigation state, which the editor clears once read: a reload or a shared link starts
 * without one.
 */
export type VisitReportFlow = { flowId: string; startedAt: number; registry?: RegistryVisit };

/**
 * A report opened as the visit of "Agregar registro": what its `Field Registry` events carry, worked
 * out when the registry hands over (plain data, so it travels in the navigation state too).
 */
export type RegistryVisit = ReturnType<typeof flowEventProps> & {
    origin: FieldRegistryOrigin;
    entry_source: FieldRegistryEntrySource;
};

export const registryVisit = (flow: FieldRegistryFlow): RegistryVisit => ({
    ...flowEventProps(flow),
    origin: flow.origin,
    entry_source: flow.entrySource,
});

/** The registry's properties every `Field Registry` event of the visit carries. */
export const registryVisitProps = ({ origin: _origin, entry_source: _entrySource, ...props }: RegistryVisit) => ({
    ...props,
    registry_type: 'visit' as const,
});

const STATE_KEY = 'visitReportFlow';

export const flowNavigationState = (flow: VisitReportFlow) => ({ [STATE_KEY]: flow });

export const flowFromNavigationState = (state: unknown): VisitReportFlow | undefined => {
    const flow = (state as Record<string, unknown> | null)?.[STATE_KEY] as VisitReportFlow | undefined;
    return typeof flow?.flowId === 'string' && typeof flow.startedAt === 'number' ? flow : undefined;
};

/**
 * The report's attempt, for the registries opened from the editor to carry as `parent_flow_id`.
 * Unset for a draft picked up again, which has none.
 */
export const VisitReportFlowContext = createContext<string | undefined>(undefined);

export const useVisitReportFlowId = () => useContext(VisitReportFlowContext);

/** Same breakpoint as `useIsMobile`, read when the event goes out. */
export const reportDevice = (): 'desktop' | 'mobile' =>
    typeof window !== 'undefined' && window.innerWidth <= 900 ? 'mobile' : 'desktop';

/** The properties every event of the report carries. */
export const reportEventProps = ({
    flowId,
    reportId,
    reportType,
}: {
    flowId?: string;
    reportId?: number;
    reportType?: 'visit' | 'remote';
}) => ({
    flow_id: flowId,
    report_id: reportId ? String(reportId) : undefined,
    report_type: reportType,
    report_version: 'new' as const,
    device: reportDevice(),
});

/**
 * Gives the attempt its id and reports it began. Opened from "Agregar registro", the report keeps
 * the registry's `flowId` so both funnels read as one attempt.
 */
export const startVisitReportFlow = (
    entrySource: VisitReportEntrySource,
    registry?: RegistryVisit
): VisitReportFlow => {
    const flow = { flowId: registry?.flow_id ?? uuidv4(), startedAt: Date.now(), registry };
    mpTrackEvent('Visit report flow:Started', {
        ...reportEventProps({ flowId: flow.flowId }),
        entry_source: entrySource,
    });
    return flow;
};

/** Read on unload too: requests are batched by default, and a queued event would miss it. */
export const BEACON = { transport: 'sendBeacon', send_immediately: true } as const;

/** The visit left without generating the report, as a `Field Registry` abandonment. */
export const trackRegistryVisitAbandoned = (
    registry: RegistryVisit,
    step: 'field_selection' | 'editor',
    exitMethod: FieldRegistryExitMethod,
    formTouched: boolean
) =>
    mpTrackEvent(
        'Field Registry Abandoned',
        {
            ...registryVisitProps(registry),
            last_step: 'form',
            exit_method: exitMethod,
            form_touched: formTouched,
            step,
        },
        BEACON
    );

export const elementType = (sectionId: SectionId) => sectionId.replace(/-/g, '_') as VisitReportElementType;

/** Whether dropping the section adds an element: "Textos, fotos o tablas" can repeat, others only once. */
export const addsElement = (sections: SectionId[], sectionId: SectionId) =>
    sectionId === TEXTS_SECTION || !sections.includes(sectionId);

/** Elements across every lote of the report. */
export const elementsCount = (fieldObservations: Pick<FieldObservation, 'sections'>[]) =>
    fieldObservations.reduce((count, fieldObservation) => count + fieldObservation.sections.length, 0);

export const secondsSince = (startedAt: number) => Math.max(0, Math.round((Date.now() - startedAt) / 1000));
